import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff, Lock, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'
import api from '../services/api'
import toast from 'react-hot-toast'

export default function Claim() {
  const { token } = useParams()
  const { isDark } = useTheme()
  const navigate = useNavigate()

  const [loading, setLoading]   = useState(true)
  const [valid, setValid]       = useState(false)
  const [error, setError]       = useState('')
  const [username, setUsername] = useState('')
  const [custName, setCustName] = useState('')

  const [code, setCode]         = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')
  const [showPw, setShowPw]     = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone]         = useState(false)

  // On mount: hit the tracked claim-link endpoint (records the click) and load context
  useEffect(() => {
    let active = true
    api.get(`/invites/claim/${token}`)
      .then(res => {
        if (!active) return
        setUsername(res.data.username || '')
        setCustName(res.data.customer_name || '')
        setValid(true)
      })
      .catch(err => {
        if (!active) return
        setError(err.response?.data?.detail || 'This invitation link is invalid or has expired.')
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (code.trim().length !== 6) { toast.error('Enter the 6-digit code from your invite.'); return }
    if (password.length < 8)      { toast.error('Password must be at least 8 characters.'); return }
    if (password !== confirm)     { toast.error('Passwords do not match.'); return }
    setSubmitting(true)
    try {
      await api.post(`/invites/claim/${token}`, { code: code.trim(), password })
      setDone(true)
      toast.success('Account claimed! You can now log in.')
      setTimeout(() => navigate('/login'), 2500)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim the account. Check your code and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 animate-fade-in">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={isDark ? "/logo-w-tall.png" : "/logo-tall.png"}
            alt="Tony's Liquor Distributor"
            className="h-28 w-auto object-contain mx-auto"
          />
        </div>

        <div className="card p-6 md:p-8 backdrop-blur-sm bg-white/90 dark:bg-gray-900/80 border border-white/60 dark:border-white/5 shadow-2xl">
          {loading && (
            <p className="text-center text-gray-500 py-8">Loading your invitation…</p>
          )}

          {!loading && error && (
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">Invitation problem</h2>
              <p className="text-sm text-gray-500 mb-6">{error}</p>
              <Link to="/login" className="text-primary hover:underline text-sm">Go to login</Link>
            </div>
          )}

          {!loading && valid && !done && (
            <>
              <div className="text-center mb-6">
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Claim your account</h1>
                {custName && <p className="text-sm text-gray-500 mt-1">{custName}</p>}
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 mb-6">
                <p className="text-xs text-gray-400 mb-1">YOUR USERNAME</p>
                <p className="text-lg font-semibold text-primary">{username}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">6-digit code</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text" inputMode="numeric" maxLength={6}
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="input pl-10 tracking-widest text-lg"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">New password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="input pl-10 pr-10"
                    />
                    <button type="button" onClick={() => setShowPw(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Confirm password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Re-enter your password"
                      className="input pl-10"
                    />
                  </div>
                </div>

                <button type="submit" disabled={submitting}
                  className="btn btn-primary w-full py-3 text-base font-semibold disabled:opacity-60">
                  {submitting ? 'Setting up…' : 'Claim account'}
                </button>
              </form>
            </>
          )}

          {done && (
            <div className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">You're all set!</h2>
              <p className="text-sm text-gray-500 mb-2">Your account has been claimed.</p>
              <p className="text-xs text-gray-400">Redirecting you to login…</p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Not for sale to persons under 18. Drink responsibly.
        </p>
      </div>
    </div>
  )
}
